<template>
  <main class="barcode-scanner-page">
    <video ref="video" autoplay playsinline muted class="barcode-scanner-video"></video>
    <button
      type="button"
      class="barcode-scanner-close"
      aria-label="Close scanner"
      @click="cancelScan"
    >
      ×
    </button>
    <div class="barcode-scanner-frame">
      <span class="barcode-scanner-corner barcode-scanner-corner-tl"></span>
      <span class="barcode-scanner-corner barcode-scanner-corner-tr"></span>
      <span class="barcode-scanner-corner barcode-scanner-corner-bl"></span>
      <span class="barcode-scanner-corner barcode-scanner-corner-br"></span>
      <span class="barcode-scanner-line"></span>
    </div>
    <p class="barcode-scanner-status">{{ status }}</p>
  </main>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { BarcodeFormat, BrowserMultiFormatOneDReader } from '@zxing/browser'
import { DecodeHintType } from '@zxing/library'

const video = ref(null)
const status = ref('Starting camera...')
const returnOrigin = new URLSearchParams(window.location.search).get('returnOrigin') || ''
let reader = null
let nativeDetector = null
let controls = null
let cameraStream = null
let restartTimer = null
let detectionTimer = null
let busy = false
let scannerActive = false

function isAllowedReturnOrigin(origin) {
  try {
    const parsed = new URL(origin)
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && !!parsed.hostname
  } catch {
    return false
  }
}

function stopScanner() {
  scannerActive = false
  if (restartTimer) {
    clearTimeout(restartTimer)
    restartTimer = null
  }
  if (detectionTimer) {
    clearTimeout(detectionTimer)
    detectionTimer = null
  }
  controls?.stop?.()
  controls = null
  nativeDetector = null
  cameraStream?.getTracks?.().forEach((track) => track.stop())
  cameraStream = null
  if (video.value) {
    video.value.pause()
    video.value.srcObject = null
  }
}

function handleBarcodeResult(result) {
  const barcode = String(result?.getText?.() || '').trim()
  if (!barcode) return
  returnBarcode(barcode, 'Scan successful.')
}

function returnBarcode(barcode, message) {
  if (busy) return
  if (!window.opener || !isAllowedReturnOrigin(returnOrigin)) {
    status.value = 'The POS window is not available.'
    stopScanner()
    return
  }

  busy = true
  stopScanner()
  window.opener.postMessage({ type: 'barcode-scan-result', barcode }, returnOrigin)
  status.value = message
  setTimeout(() => window.close(), 50)
}

function cancelScan() {
  returnBarcode('', 'Scan cancelled.')
}

async function startScanner() {
  if (!isAllowedReturnOrigin(returnOrigin)) {
    status.value = 'Invalid POS return address.'
    return
  }

  try {
    await nextTick()
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Camera access is not available in this browser.')
    }

    cameraStream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30, max: 60 },
      },
      audio: false,
    })
    const videoElement = video.value
    if (!videoElement) throw new Error('Camera preview is unavailable.')

    videoElement.srcObject = cameraStream
    await videoElement.play()
    if (!videoElement.videoWidth || !videoElement.videoHeight) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup()
          reject(new Error('Camera preview did not start.'))
        }, 10000)
        const onLoaded = () => {
          if (!videoElement.videoWidth || !videoElement.videoHeight) return
          cleanup()
          resolve()
        }
        const cleanup = () => {
          clearTimeout(timeout)
          videoElement.removeEventListener('loadedmetadata', onLoaded)
          videoElement.removeEventListener('resize', onLoaded)
        }
        videoElement.addEventListener('loadedmetadata', onLoaded)
        videoElement.addEventListener('resize', onLoaded)
        onLoaded()
      })
    }
    await optimizeCameraTrack(cameraStream.getVideoTracks()[0])

    const hints = new Map([
      [DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.CODABAR,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.CODE_128,
        BarcodeFormat.EAN_8,
        BarcodeFormat.EAN_13,
        BarcodeFormat.ITF,
        BarcodeFormat.RSS_14,
        BarcodeFormat.RSS_EXPANDED,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
      ]],
      [DecodeHintType.TRY_HARDER, true],
    ])
    scannerActive = true
    nativeDetector = await createNativeBarcodeDetector()
    if (nativeDetector) {
      scanWithNativeDetector(videoElement)
    } else {
      reader = new BrowserMultiFormatOneDReader(hints, {
        delayBetweenScanAttempts: 100,
        delayBetweenScanSuccess: 150,
      })
      await startDecodeLoop(videoElement)
    }
    if (!busy) status.value = 'Scanning... Align a 1D barcode inside the frame.'
  } catch (error) {
    stopScanner()
    status.value = error?.name === 'NotAllowedError'
      ? 'Camera permission was denied.'
      : `Camera could not be started: ${error?.message || 'Please try again.'}`
  }
}

async function optimizeCameraTrack(track) {
  if (!track?.applyConstraints || !track.getCapabilities) return

  let capabilities
  try {
    capabilities = track.getCapabilities()
  } catch (error) {
    console.warn('Camera capabilities could not be read:', error)
    return
  }
  const settings = track.getSettings?.() || {}
  const focusModes = capabilities.focusMode
  if (Array.isArray(focusModes) && focusModes.includes('continuous')) {
    try {
      await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
    } catch (error) {
      console.warn('Continuous camera autofocus could not be enabled:', error)
    }
  }

  const zoomRange = capabilities.zoom
  if (zoomRange && Number.isFinite(zoomRange.min) && Number.isFinite(zoomRange.max)) {
    const targetZoom = Math.max(zoomRange.min, Math.min(2, zoomRange.max))
    if (targetZoom > (settings.zoom || zoomRange.min)) {
      try {
        await track.applyConstraints({ advanced: [{ zoom: targetZoom }] })
      } catch (error) {
        console.warn('Camera zoom could not be applied:', error)
      }
    }
  }

  const exposureModes = capabilities.exposureMode
  if (Array.isArray(exposureModes) && exposureModes.includes('continuous')) {
    try {
      await track.applyConstraints({ advanced: [{ exposureMode: 'continuous' }] })
    } catch (error) {
      console.warn('Continuous camera exposure could not be enabled:', error)
    }
  }
}

async function createNativeBarcodeDetector() {
  const Detector = window.BarcodeDetector
  if (!Detector) return null

  try {
    const formats = [
      'codabar',
      'code_39',
      'code_93',
      'code_128',
      'ean_8',
      'ean_13',
      'itf',
      'rss_14',
      'rss_expanded',
      'upc_a',
      'upc_e',
    ]
    const supportedFormats = typeof Detector.getSupportedFormats === 'function'
      ? await Detector.getSupportedFormats()
      : formats
    const supported = formats.filter((format) => supportedFormats.includes(format))
    return supported.length ? new Detector({ formats: supported }) : null
  } catch (error) {
    console.warn('Native barcode detection is unavailable; using ZXing:', error)
    return null
  }
}

async function scanWithNativeDetector(videoElement) {
  if (!scannerActive || busy || !nativeDetector) return

  try {
    const results = await nativeDetector.detect(videoElement)
    if (!scannerActive || busy) return
    const barcode = results.find((result) => result.rawValue)?.rawValue
    if (barcode) {
      returnBarcode(String(barcode).trim(), 'Scan successful.')
      return
    }
    detectionTimer = setTimeout(() => scanWithNativeDetector(videoElement), 80)
  } catch (error) {
    console.warn('Native barcode detection failed; switching to ZXing:', error)
    nativeDetector = null
    if (!scannerActive || busy) return
    reader = new BrowserMultiFormatOneDReader(new Map([
      [DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.CODABAR,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.CODE_128,
        BarcodeFormat.EAN_8,
        BarcodeFormat.EAN_13,
        BarcodeFormat.ITF,
        BarcodeFormat.RSS_14,
        BarcodeFormat.RSS_EXPANDED,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
      ]],
      [DecodeHintType.TRY_HARDER, true],
    ]), {
      delayBetweenScanAttempts: 100,
      delayBetweenScanSuccess: 150,
    })
    status.value = 'Using compatibility scanner...'
    startDecodeLoop(videoElement).catch((retryError) => {
      status.value = `Scanner error: ${retryError?.message || 'Please try again.'}`
    })
  }
}

async function startDecodeLoop(videoElement) {
  if (!scannerActive || busy || !reader) return

  try {
    controls = await reader.decodeFromVideoElement(videoElement, (result, error) => {
      if (result) {
        handleBarcodeResult(result)
        return
      }
      if (
        error
        && !['NotFoundException', 'ChecksumException', 'FormatException'].includes(error.name)
        && scannerActive
        && !restartTimer
      ) {
        status.value = 'Scanner retrying... Keep the barcode in view.'
        restartTimer = setTimeout(() => {
          restartTimer = null
          startDecodeLoop(videoElement).catch((retryError) => {
            status.value = `Scanner error: ${retryError?.message || 'Please try again.'}`
          })
        }, 300)
      }
    })
  } catch (error) {
    if (!scannerActive || busy) return
    status.value = `Scanner error: ${error?.message || 'Please try again.'}`
    restartTimer = setTimeout(() => {
      restartTimer = null
      startDecodeLoop(videoElement).catch((retryError) => {
        status.value = `Scanner error: ${retryError?.message || 'Please try again.'}`
      })
    }, 500)
  }
}

onMounted(startScanner)
onUnmounted(stopScanner)
</script>

<style scoped>
.barcode-scanner-page {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: #000;
  color: #fff;
}

.barcode-scanner-close {
  position: absolute;
  top: max(16px, env(safe-area-inset-top));
  right: max(16px, env(safe-area-inset-right));
  z-index: 2;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 9999px;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.barcode-scanner-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.barcode-scanner-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(82vw, 360px);
  height: min(42vw, 180px);
  transform: translate(-50%, -50%);
  border-radius: 12px;
  border: 1px solid rgba(74, 222, 128, 0.25);
  pointer-events: none;
}

.barcode-scanner-corner {
  position: absolute;
  width: 28px;
  height: 28px;
  border: 3px solid #22c55e;
}

.barcode-scanner-corner-tl {
  top: 0;
  left: 0;
  border-right: 0;
  border-bottom: 0;
  border-top-left-radius: 10px;
}

.barcode-scanner-corner-tr {
  top: 0;
  right: 0;
  border-left: 0;
  border-bottom: 0;
  border-top-right-radius: 10px;
}

.barcode-scanner-corner-bl {
  bottom: 0;
  left: 0;
  border-right: 0;
  border-top: 0;
  border-bottom-left-radius: 10px;
}

.barcode-scanner-corner-br {
  right: 0;
  bottom: 0;
  border-left: 0;
  border-top: 0;
  border-bottom-right-radius: 10px;
}

.barcode-scanner-line {
  position: absolute;
  top: 50%;
  left: 8%;
  right: 8%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #4ade80, transparent);
  animation: barcode-scan-move 1.8s ease-in-out infinite;
}

.barcode-scanner-status {
  position: absolute;
  right: 1rem;
  bottom: 2rem;
  left: 1rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  font-size: 0.75rem;
  line-height: 1.25rem;
}

@keyframes barcode-scan-move {
  0% { transform: translateY(-40px); opacity: 0.35; }
  50% { opacity: 1; }
  100% { transform: translateY(40px); opacity: 0.35; }
}
</style>
